import React, { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Dual-input for image: paste a URL OR upload a file.
 * Calls onChange(url) with the resolved URL.
 */
export default function ImageUploadField({ value, onChange, testid = "image-field", required = false }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  async function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Backend returns relative URL "/api/files/...". Make absolute.
      const absUrl = r.data.url.startsWith("http")
        ? r.data.url
        : `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      onChange(absUrl);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  }

  return (
    <div>
      <div className="flex gap-2">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder="https://… or upload"
          data-testid={testid}
          className="flex-1 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
        />
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          data-testid={`${testid}-upload`}
          className="border border-slate-900 bg-white px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-slate-900 hover:text-white transition flex items-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={ref}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          onChange={handleFile}
          data-testid={`${testid}-file`}
        />
      </div>
      {value && (
        <div className="mt-2 border border-slate-200 p-2 inline-block">
          <img src={value} alt="" className="h-24 object-cover" />
        </div>
      )}
    </div>
  );
}
